import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, List, ListItem, ListItemIcon, ListItemText, Card, CardMedia, IconButton, styled } from '@mui/material';
import { CloudUpload, InsertDriveFile, Close } from '@mui/icons-material';

// iOS-kompatible Upload-Lösung ohne accept-Attribute
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxSize?: number;
  accept?: string | Record<string, string[]>;
  multiple?: boolean;
  helperText?: string;
  label?: string;
  onChange?: (files: File[]) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  maxFiles = 5,
  maxSize = 10 * 1024 * 1024, // 10MB
  accept,
  multiple = false,
  helperText = 'Dateien hier ablegen oder klicken zum Auswählen',
  label,
  onChange,
}) => {
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Create preview URLs when files change
  useEffect(() => {
    const newPreviews = files.map(file => {
      if (file.type.startsWith('image/')) {
        return URL.createObjectURL(file);
      }
      return '';
    });
    setPreviews(newPreviews);

    // Cleanup URLs on component unmount or files change
    return () => {
      newPreviews.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [files]);

  // iOS-kompatible Upload-Handler
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const newFiles = multiple ? [...files, ...selectedFiles] : selectedFiles;
    
    if (newFiles.length > maxFiles) {
      alert(`Maximal ${maxFiles} Dateien erlaubt`);
      return;
    }

    const oversizedFiles = selectedFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      alert(`Einige Dateien sind größer als ${maxSize / 1024 / 1024}MB`);
      return;
    }

    onFilesChange(newFiles);
    if (onChange) {
      onChange(newFiles);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
    if (onChange) {
      onChange(newFiles);
    }
  };

  // Bestimme ob Bilder akzeptiert werden (ohne accept-Attribut)
  const isImageAccepted = !accept || (typeof accept === 'string' && accept.includes('image')) || 
    (typeof accept === 'object' && Object.keys(accept).some(key => key.includes('image')));

  return (
    <Box>
      {/* iOS-kompatibler Upload-Button ohne accept-Attribut */}
      <Button
        variant="outlined"
        startIcon={<CloudUpload />}
        sx={{ mb: 1 }}
        onClick={handleUploadClick}
      >
        {label || 'Dateien auswählen'}
      </Button>
      
      {/* Versteckter File-Input ohne accept-Attribut für iOS-Kompatibilität */}
      <VisuallyHiddenInput
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        multiple={multiple}
        // KEINE accept-Attribute mehr - das verursacht iOS-Probleme
      />
      
      <Typography variant="body2" color="textSecondary">
        {helperText}
      </Typography>
      
      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {isImageAccepted ? (
            // Image preview grid
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 2 }}>
              {files.map((file, index) => (
                <Box key={index}>
                  <Card sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="150"
                      image={previews[index]}
                      alt={file.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)'
                        }
                      }}
                      onClick={() => removeFile(index)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        p: 1,
                        fontSize: '0.7rem'
                      }}
                    >
                      {file.name}
                    </Typography>
                  </Card>
                </Box>
              ))}
            </Box>
          ) : (
            // File list for non-images
            <List>
              {files.map((file, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <InsertDriveFile />
                  </ListItemIcon>
                  <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(2)} KB`} />
                  <IconButton edge="end" onClick={() => removeFile(index)}>
                    <Close />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
}; 